import {
    createDefaultCameraZoomSetting,
    createDefaultDisplaySettings,
    resolveWheelZoomLevel,
    type CameraZoomSetting,
    type DisplaySettings,
} from '@sorrem/shared';
import type {
    GameMapSnapshot,
    UIGameState,
    UIDebugOverlaySettings,
} from '@sorrem-client/types';
import { DEBUG_OVERLAY_SETTINGS_KEY, getStoredValue, storeValue } from '@sorrem-client/ui/shared/browser-storage';
import { DEFAULT_UI_GAME_STATE } from '@sorrem-client/ui/shared/game-state-defaults';
import { replaceEqualDeep } from '@sorrem-client/ui/shared/game-state-sharing';

type Listener = (state: UIGameState) => void;
type MapListener = (map: GameMapSnapshot | null) => void;

/**
 * Something the UI tells the running game, as opposed to state the game
 * reports to the UI. Player input is not here: it goes through the page's
 * input capture layer (`ui/input/page-input.ts`).
 */
export type GameUiInput =
    | { readonly kind: 'debug-overlay-settings'; readonly settings: UIDebugOverlaySettings }
    | { readonly kind: 'display-settings'; readonly settings: DisplaySettings }
    | { readonly kind: 'reduced-motion'; readonly reducedMotion: boolean }
    | { readonly kind: 'camera-zoom'; readonly level: number };
type GameUiInputListener = (input: GameUiInput) => void;
type CameraZoomWheelListener = (level: number) => void;

function getStoredDebugOverlaySettings(): UIDebugOverlaySettings | null {
    const raw = getStoredValue('local', DEBUG_OVERLAY_SETTINGS_KEY);
    if (raw === undefined) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed == null || typeof parsed !== 'object') {
            return null;
        }

        const candidate = parsed as Partial<UIDebugOverlaySettings>;
        if (
            typeof candidate.telemetryEnabled !== 'boolean'
            || typeof candidate.entityInfoEnabled !== 'boolean'
        ) {
            return null;
        }

        return {
            telemetryEnabled: candidate.telemetryEnabled,
            entityInfoEnabled: candidate.entityInfoEnabled,
        };
    } catch {
        return null;
    }
}

function storeDebugOverlaySettings(settings: UIDebugOverlaySettings): void {
    storeValue('local', DEBUG_OVERLAY_SETTINGS_KEY, JSON.stringify(settings));
}

export class GameStateBridge {
    private state: UIGameState;
    private listeners: Set<Listener> = new Set();
    private map: GameMapSnapshot | null = null;
    private mapListeners: Set<MapListener> = new Set();
    /** The account's display settings, as the run HUD last reported them; read when a game starts. */
    private displaySettings: DisplaySettings = createDefaultDisplaySettings();
    private cameraZoom: CameraZoomSetting = createDefaultCameraZoomSetting();
    private cameraZoomWheelListeners: Set<CameraZoomWheelListener> = new Set();

    constructor() {
        const storedDebugOverlaySettings = getStoredDebugOverlaySettings();
        this.state = {
            ...DEFAULT_UI_GAME_STATE,
            player: { ...DEFAULT_UI_GAME_STATE.player },
            debugOverlaySettings: storedDebugOverlaySettings ?? DEFAULT_UI_GAME_STATE.debugOverlaySettings,
        };
    }

    private gameInputListeners: Set<GameUiInputListener> = new Set();

    getState(): UIGameState {
        return this.state;
    }

    /**
     * Merges a patch into the state. Patches from the game worker are
     * structured clones, so each part keeps its old reference when its values
     * didn't change (and listeners hear nothing when no part did): selectors
     * compared with `Object.is` then only fire on real changes.
     */
    update(partial: Partial<UIGameState>): void {
        const next = replaceEqualDeep(this.state, { ...this.state, ...partial });
        if (next === this.state) {
            return;
        }
        this.state = next;
        for (const fn of this.listeners) {
            fn(this.state);
        }
    }

    getMap(): GameMapSnapshot | null {
        return this.map;
    }

    updateMap(nextMap: GameMapSnapshot | null): void {
        this.map = nextMap;
        for (const fn of this.mapListeners) {
            fn(this.map);
        }
    }

    setDebugOverlaySettings(nextSettings: Partial<UIDebugOverlaySettings>): void {
        const mergedSettings: UIDebugOverlaySettings = {
            ...this.state.debugOverlaySettings,
            ...nextSettings,
        };

        this.update({
            debugOverlaySettings: {
                ...mergedSettings,
            },
        });

        storeDebugOverlaySettings(mergedSettings);
        this.emitGameInput({ kind: 'debug-overlay-settings', settings: mergedSettings });
    }

    /** Receives what the UI tells the game (debug settings). Used by the game session host. */
    getDisplaySettings(): DisplaySettings {
        return this.displaySettings;
    }

    /** Tells the running game the account's display settings (#200, #202). */
    setDisplaySettings(settings: DisplaySettings): void {
        this.displaySettings = settings;
        this.emitGameInput({ kind: 'display-settings', settings });
    }

    /** Tells the running game whether the player prefers reduced motion (#266). */
    setReducedMotion(reducedMotion: boolean): void {
        this.emitGameInput({ kind: 'reduced-motion', reducedMotion });
    }

    getCameraZoom(): CameraZoomSetting {
        return this.cameraZoom;
    }

    /** Tells the running game the camera zoom: the account's, or a level the settings slider is being dragged through. */
    setCameraZoom(setting: CameraZoomSetting): void {
        const levelChanged = setting.level !== this.cameraZoom.level;
        this.cameraZoom = setting;
        if (levelChanged) {
            this.emitGameInput({ kind: 'camera-zoom', level: setting.level });
        }
    }

    /**
     * A mouse-wheel turn of `deltaY` pixels over the game: zooms, unless the
     * zoom is locked, and tells the wheel listeners the new level so the
     * settings save it.
     */
    wheelCameraZoom(deltaY: number): void {
        if (this.cameraZoom.isLocked) {
            return;
        }
        const level = resolveWheelZoomLevel(this.cameraZoom.level, deltaY);
        if (level === this.cameraZoom.level) {
            return;
        }
        this.setCameraZoom({ ...this.cameraZoom, level });
        for (const fn of this.cameraZoomWheelListeners) {
            fn(level);
        }
    }

    subscribeCameraZoomWheel(fn: CameraZoomWheelListener): () => void {
        this.cameraZoomWheelListeners.add(fn);
        return () => this.cameraZoomWheelListeners.delete(fn);
    }

    subscribeGameInput(fn: GameUiInputListener): () => void {
        this.gameInputListeners.add(fn);
        return () => this.gameInputListeners.delete(fn);
    }

    private emitGameInput(input: GameUiInput): void {
        for (const fn of this.gameInputListeners) {
            fn(input);
        }
    }

    subscribe(fn: Listener): () => void {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    subscribeMap(fn: MapListener): () => void {
        this.mapListeners.add(fn);
        return () => this.mapListeners.delete(fn);
    }
}
